/**
 * What went wrong on somebody else's phone.
 *
 * Eleven people use this app and ten of them cannot tell anybody what they
 * saw. "It didn't work" is the whole report, and every fault so far has been
 * diagnosed by guessing and then asking Carter to try again. A JavaScript
 * error on Mom's phone leaves no trace anywhere: the console is on a device
 * nobody can open, and the page that threw is gone by the time it is
 * mentioned.
 *
 * So the phone writes it down. What lands is deliberately small — a message, a
 * stack, which build, which screen — and readable only by an admin, because a
 * stack trace can carry whatever happened to be in scope.
 *
 * The pure parts are separated out and tested. The rest is a Firestore write
 * that must never itself throw: an error reporter that can fail while
 * reporting turns one broken screen into a loop.
 */
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { COL, ERROR_RETENTION_DAYS } from '../../shared/constants';
import type { ErrorDoc } from '../../shared/types';
import { db } from './firebase';

declare const __SW_VERSION__: string;

/**
 * Enough to see a pattern, few enough that a render loop cannot bill the
 * family for a thousand writes. Counted per page rather than per day, because
 * the loop that matters is the one happening right now.
 */
const MOST_PER_PAGE = 8;

/** How much of a stack is worth keeping. Beyond this it is all framework. */
const STACK_LIMIT = 4000;
const MESSAGE_LIMIT = 500;

export interface Told {
  message: string;
  stack?: string;
  kind: ErrorDoc['kind'];
}

/**
 * A thrown thing turned into something readable.
 *
 * Anything at all can be thrown — a string, a number, undefined, an object
 * with no message. The report has to say something useful about all of them,
 * because the ones that are not Errors are exactly the ones nobody has looked
 * at.
 */
export function describeError(thrown: unknown, kind: ErrorDoc['kind']): Told {
  if (thrown instanceof Error) {
    return {
      message: cap(thrown.message || thrown.name || 'Error', MESSAGE_LIMIT),
      ...(thrown.stack ? { stack: cap(thrown.stack, STACK_LIMIT) } : {}),
      kind,
    };
  }

  if (typeof thrown === 'string') {
    return { message: cap(thrown || 'Empty error', MESSAGE_LIMIT), kind };
  }

  if (thrown && typeof thrown === 'object') {
    const maybe = thrown as { message?: unknown; stack?: unknown };
    const message =
      typeof maybe.message === 'string' && maybe.message
        ? maybe.message
        : safely(thrown);
    return {
      message: cap(message, MESSAGE_LIMIT),
      ...(typeof maybe.stack === 'string' ? { stack: cap(maybe.stack, STACK_LIMIT) } : {}),
      kind,
    };
  }

  return { message: cap(`Threw ${String(thrown)}`, MESSAGE_LIMIT), kind };
}

function cap(text: string, limit: number): string {
  return text.length <= limit ? text : `${text.slice(0, limit - 1)}…`;
}

/** An object with no message of its own, described rather than dropped. */
function safely(value: object): string {
  try {
    return JSON.stringify(value) || 'Unreadable error';
  } catch {
    return 'Unreadable error';
  }
}

/**
 * Whether this one is worth writing down.
 *
 * Two questions at once, and both are about loops rather than about the
 * error. The same message arriving over and over is one fault, not fifty, and
 * a render that throws on every frame would otherwise write until the budget
 * ran out. Held as data so it can be tested without a browser.
 */
export function shouldTell(
  told: Told,
  seen: Set<string>,
  count: number,
): boolean {
  if (count >= MOST_PER_PAGE) return false;
  return !seen.has(told.message);
}

let seen = new Set<string>();
let count = 0;

/*
 * Where the phone was when it went wrong. A bare "Script error." from Ethan's
 * phone said nothing else -- not even the screen, which on the home screen
 * was an empty hash -- so there was nothing to go on. Kept small: the screen,
 * how long the app had been open, whether it had only just come back from
 * the background, the screens before, and the script at fault if the browser
 * will say.
 */
export interface Context {
  screen: string;
  openForMs: number;
  /** Since the app last came back to the front, if it has. */
  backForMs: number | null;
  before: string[];
  /** The script the browser blames, and where in it; empty when it will not say. */
  source?: string;
}

export function describeContext(context: Context): string {
  const parts = [
    context.screen || '#/ (home)',
    `open ${Math.round(context.openForMs / 1000)}s`,
  ];
  // Only worth saying when it is the likely cause: a page waking up.
  if (context.backForMs !== null && context.backForMs < 30_000) {
    parts.push(`back from background ${Math.round(context.backForMs / 1000)}s ago`);
  }
  if (context.before.length) parts.push(`before: ${context.before.join(' > ')}`);
  if (context.source) parts.push(`in ${context.source}`);
  return cap(parts.join(' · '), 300);
}

const openedAt = Date.now();
let backAt: number | null = null;
const screens: string[] = [];
if (typeof window !== 'undefined') {
  window.addEventListener('hashchange', (event) => {
    let was: string;
    try {
      was = new URL(event.oldURL).hash || '#/ (home)';
    } catch {
      // A hashchange made by hand can come without one, and a logger that
      // throws reports itself: it was most of what the log held.
      return;
    }
    screens.push(was);
    if (screens.length > 3) screens.shift();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') backAt = Date.now();
  });
}

function contextNow(source?: string): string {
  return describeContext({
    screen: window.location.hash,
    openForMs: Date.now() - openedAt,
    backForMs: backAt === null ? null : Date.now() - backAt,
    before: [...screens],
    source,
  });
}

/** The script an ErrorEvent blames, when the browser has not muted it. */
export function sourceOf(event: Pick<ErrorEvent, 'filename' | 'lineno' | 'colno'>): string | undefined {
  if (!event.filename) return undefined;
  return `${event.filename}:${event.lineno}:${event.colno}`;
}

/** Only for tests, which would otherwise carry one page's memory into the next. */
export function forgetToldErrors(): void {
  seen = new Set();
  count = 0;
}

/**
 * Writes one down, and never throws while doing it.
 *
 * A reporter that can fail is worse than none: the failure is itself an
 * unhandled rejection, which arrives back here, which fails again.
 */
export async function tell(
  uid: string,
  thrown: unknown,
  kind: ErrorDoc['kind'] = 'error',
  where = typeof window === 'undefined' ? '' : contextNow(),
): Promise<void> {
  try {
    if (!uid) return;

    const told = describeError(thrown, kind);
    if (!shouldTell(told, seen, count)) return;

    seen.add(told.message);
    count += 1;

    const doc: ErrorDoc = {
      uid,
      at: serverTimestamp(),
      message: told.message,
      kind: told.kind,
      where,
      build: typeof __SW_VERSION__ === 'string' ? __SW_VERSION__ : 'unknown',
      agent: cap(navigator.userAgent, 300),
      expireAt: new Date(Date.now() + ERROR_RETENTION_DAYS * 86_400_000),
      ...(told.stack ? { stack: told.stack } : {}),
    };

    await addDoc(collection(db, COL.errors), doc);
  } catch {
    // Nothing. There is nowhere left to report a failure to report.
  }
}

/**
 * Listens for the two ways a browser surfaces something nobody caught.
 *
 * index.html already catches what throws before the modules load, and draws it
 * on the screen, because at that point there is no app to report through. This
 * is for everything after: by then there is a signed-in member to attribute it
 * to and a database to put it in.
 */
export function watchForErrors(uid: string): () => void {
  const onError = (event: ErrorEvent) => {
    void tell(uid, event.error ?? event.message, 'error', contextNow(sourceOf(event)));
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    void tell(uid, event.reason, 'rejection');
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}
